/**
 * aethereum guide — a short, copy-friendly "how to coordinate in this room" loop
 * for a developer and their agents. Read-only, offline-safe (it only prints), and
 * brand-consistent. No em dashes in user-facing copy.
 */

import { BRAND } from "@br9704/aethereum-shared";

/** The full guide text, returned as a string so it is unit-testable. */
export function guideText(): string {
  const n = BRAND.name;
  const rule = "  " + "─".repeat(58);
  return [
    "",
    `   ⬡  ${BRAND.logo}   how to coordinate in this room`,
    rule,
    "",
    "   THE LOOP (your agents run it on their own, you can call it by hand too)",
    "",
    "     1. share_intent      say what you are about to build, so teammates'",
    "                          agents route around you.",
    "     2. declare_contract  register every interface you create or change",
    "                          (an API route, a shared type, an event shape).",
    "     3. get_team_context  ALWAYS read this before you build: who is doing",
    "                          what, which contracts exist, and what changed.",
    "     4. negotiate, do not break   when a contract others depend on must",
    "                          change, propose_contract so dependents accept or",
    "                          push back, then finalize_proposal. Never silently",
    "                          redeclare a shape others rely on.",
    "     5. claim an area      claim the part of the codebase you are working",
    "                          in (release when done) so two agents do not",
    "                          collide on the same files.",
    "",
    "   ALERTS + MESSAGES (these come to you, you do not poll)",
    "     - A collision alert surfaces the moment a contract you depend on",
    "       changes, or two agents claim the same area. Re-sync before you write.",
    "     - A direct message from a teammate's agent (send_message) shows up the",
    "       same way. Reads are exactly-once, so nothing is missed or repeated.",
    "",
    "   MISSION CONTROL (steer the fleet from one place)",
    `     ${n} directive set "<text>"   one standing order, pinned atop every`,
    "                               agent's context until you clear it.",
    `     ${n} ticket create "<title>"  open a unit of work; agents claim it`,
    "                               and move it doing then done.",
    "     share_plan                a team checklist (todo / doing / done) so",
    "                               everyone sees the same ordered next steps.",
    "",
    "   BRING IN A TEAMMATE (join codes)",
    `     Every room has a short 6-char join code (printed by '${n} init', and`,
    "     shown in get_team_context). Share it with a teammate; they run",
    `     'npx ${n} join <code>' to add their agent to this same room.`,
    "",
    "   ACCOUNTLESS vs SIGNED IN",
    `     ${n} init makes a free, accountless room instantly. No signup. To keep`,
    `     it forever and invite teammates by email, run '${n} claim' after you`,
    `     sign in. Until then it stays a private, throwaway room.`,
    "",
    "   NEXT",
    `     ${n} init     wire this project (free room, zero signup)`,
    `     ${n} watch    watch the room live in your terminal`,
    `     ${n} invite   bring a teammate or another machine into the room`,
    "",
    `   docs: ~/docs            ${BRAND.repo}`,
    "",
  ].join("\n");
}

export interface GuideOptions {
  /** Injectable sink for tests. Defaults to console.log. */
  out?: (line: string) => void;
}

/** Print the coordination guide. Never throws, never reaches the network. */
export async function runGuide(options: GuideOptions = {}): Promise<void> {
  const out = options.out ?? console.log;
  out(guideText());
}
